// Copyright (C) AirWorks Solutions, Inc - All Rights Reserved
// DO NOT REDISTRIBUTE
// UNAUTHORIZED COPYING OF THIS FILE, ANY PART OR WHOLE, VIA ANY MEDIUM IS STRICTLY PROHIBITED
// PROPRIETARY AND CONFIDENTIAL

import React, { useState } from 'react';
import moment, { Moment } from 'moment-timezone';
import { Box, useTheme } from '@mui/system';
import { Typography, Divider, Button, Fade, CircularProgress, TextField } from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { useAppDispatch, useAppSelector } from 'Hooks';
import { RootState } from 'Store';
import { GetOrgSubscriptionThunk, GetOrgThunk, IUpdateExpressAIRequest, UpdateExpressAIThunk } from 'Features/admin/adminThunk';
import { credits as formatCredits } from 'Utils/numberFormat';
import HistoryList from 'Components/HistoryList';
import ConfirmDialog from 'Components/ConfirmDialog';
import stylesDef from './styles';

moment.tz.setDefault('America/New_York');

type FormMode = 'grant' | 'change' | 'end' | null;

const ACTION_LABELS: Record<ExpressAIHistoryAction, string> = {
  grant: 'Granted',
  change: 'Changed',
  end: 'Ended',
  replaced_by_stripe: 'Replaced by Stripe subscription',
};

// A grant is in force from its start up to, not including, its end — the server's rule
export const isGrantActive = (grant?: IExpressAIGrant | null, now: Moment = moment()) =>
  !!grant && moment(grant.startDate).isSameOrBefore(now) && now.isBefore(moment(grant.endDate));

// Above 0 with at most two decimals, as the server accepts
const isValidCredits = (value: number) => Number.isFinite(value) && value > 0 && Math.round(value * 100) / 100 === value;

const formatDate = (date?: string) => (date ? moment.utc(date).format('LL') : '');

/**
 * AirWorks Express on the account (AUT-6630): a Stripe subscription (read only — change it in Stripe), a grant made
 * here in Treasure Chest, an ended grant, or nothing. Admins with pricing access grant, change and end it with a
 * note; nothing is written to Stripe. Every change is in the history below.
 */
export const ExpressAI = () => {
  const theme = useTheme();
  const styles = stylesDef(theme);
  const dispatch = useAppDispatch();

  const { user } = useAppSelector((state: RootState) => state.auth);
  const { org, expressAISubscription } = useAppSelector((state: RootState) => state.admin);
  const grant = org?.expressAIGrant || null;
  const history = org?.expressAIHistory || [];
  const balance = org?.availableExpressAICreditAcreage || 0;
  const granted = isGrantActive(grant);
  const used = granted && grant ? Math.max(0, grant.credits - balance) : 0;
  // a summary without a source comes from a server that predates grants, where all Express was Stripe
  const fromStripe = !!expressAISubscription && Object.keys(expressAISubscription).length > 0 && expressAISubscription.source !== 'treasure_chest';
  const summaryUnavailable = !expressAISubscription;

  const [mode, setMode] = useState<FormMode>(null);
  const [creditsInput, setCreditsInput] = useState('');
  const [startDate, setStartDate] = useState<Moment>(moment().startOf('day'));
  const [endDate, setEndDate] = useState<Moment>(moment().startOf('day').add(1, 'year'));
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [confirmingEnd, setConfirmingEnd] = useState(false);

  const enteredCredits = Number(creditsInput);
  const creditsValid = creditsInput.trim() !== '' && isValidCredits(enteredCredits);
  const belowUsed = mode === 'change' && creditsValid && enteredCredits < used;
  const creditsChanged = mode === 'change' && !!grant && creditsValid && enteredCredits !== grant.credits;
  const endChanged = mode === 'change' && !!grant && !endDate.isSame(moment(grant.endDate));
  const endInFuture = endDate.isAfter(moment());

  let canSave = !!note.trim() && !saving;
  if (mode === 'grant') {
    canSave = canSave && creditsValid && startDate.isSameOrBefore(moment()) && endDate.isAfter(startDate) && endInFuture;
  } else if (mode === 'change') {
    canSave = canSave && creditsValid && !belowUsed && endInFuture && (creditsChanged || endChanged);
  }

  const openForm = (nextMode: FormMode) => {
    const today = moment().startOf('day');
    setCreditsInput(nextMode === 'change' && grant ? String(grant.credits) : '');
    setStartDate(today);
    setEndDate(nextMode === 'change' && grant ? moment(grant.endDate) : moment(today).add(1, 'year'));
    setNote('');
    setError('');
    setMode(nextMode);
  };

  const closeForm = () => {
    setMode(null);
    setError('');
  };

  const submit = async (request: IUpdateExpressAIRequest) => {
    setSaving(true);
    setError('');
    const result = await dispatch(UpdateExpressAIThunk(request));
    if (result.success) {
      // the section closes on the new state, not before it
      await Promise.all([dispatch(GetOrgThunk(org._id)), dispatch(GetOrgSubscriptionThunk(org._id))]);
      setMode(null);
    } else {
      setError(result.message || 'Failed to update AirWorks Express');
    }
    setConfirmingEnd(false);
    setSaving(false);
  };

  const save = () => {
    if (mode === 'grant') {
      submit({ action: 'grant', credits: enteredCredits, startDate: startDate.toISOString(), endDate: endDate.toISOString(), note: note.trim() });
    } else if (mode === 'change') {
      submit({
        action: 'change',
        ...(creditsChanged && { credits: enteredCredits }),
        ...(endChanged && { endDate: endDate.toISOString() }),
        note: note.trim(),
      });
    } else if (mode === 'end') {
      setConfirmingEnd(true);
    }
  };

  const grantButton = (
    <Box sx={styles.planButtonGroup}>
      <Button disabled={!user?.pricingAccess} sx={styles.planButton} onClick={() => openForm('grant')} color="primary" variant="outlined">
        Grant Express
      </Button>
    </Box>
  );

  const renderStatus = () => {
    if (fromStripe) {
      const starting = expressAISubscription.expressAIStartingCredits || 0;
      const remaining = expressAISubscription.expressAIRemainingCredits || 0;
      const stripeUsed = typeof expressAISubscription.expressAIUsedCredits === 'number' ? expressAISubscription.expressAIUsedCredits : starting - remaining;
      return (
        <>
          <Typography sx={styles.headline}>{expressAISubscription.name}</Typography>
          <Typography variant="body2" sx={styles.helperText}>
            {`From Stripe subscription ${expressAISubscription.subscriptionId}: change or cancel it in Stripe.`}
          </Typography>
          <Box sx={styles.listContent}>
            <Typography>Start</Typography>
            <Typography>{formatDate(expressAISubscription.expressAICurrentPeriodStart)}</Typography>
            <Typography>Renews</Typography>
            <Typography>{formatDate(expressAISubscription.expressAICurrentPeriodEnd)}</Typography>
            <Typography>Starting credits</Typography>
            <Typography>{formatCredits(starting)}</Typography>
            <Typography>Used credits</Typography>
            <Typography>{formatCredits(Math.max(0, stripeUsed))}</Typography>
            <Typography>Remaining credits</Typography>
            <Typography>{formatCredits(remaining)}</Typography>
          </Box>
        </>
      );
    }
    if (granted && grant) {
      return (
        <>
          <Typography sx={styles.headline}>AirWorks Express · granted in Treasure Chest</Typography>
          <Box sx={styles.listContent}>
            <Typography>Start</Typography>
            <Typography>{formatDate(grant.startDate)}</Typography>
            <Typography>Ends</Typography>
            <Typography>{formatDate(grant.endDate)}</Typography>
            <Typography>Credits</Typography>
            <Typography>{formatCredits(grant.credits)}</Typography>
            <Typography>Used credits</Typography>
            <Typography>{formatCredits(used)}</Typography>
            <Typography>Remaining credits</Typography>
            <Typography>{formatCredits(balance)}</Typography>
          </Box>
          {!mode && (
            <Box sx={styles.planButtonGroup}>
              <Button disabled={!user?.pricingAccess} sx={styles.planButton} onClick={() => openForm('change')} color="primary" variant="outlined">
                Change
              </Button>
              <Button disabled={!user?.pricingAccess} sx={styles.planButton} onClick={() => openForm('end')} color="error" variant="outlined">
                End Express
              </Button>
            </Box>
          )}
        </>
      );
    }
    if (summaryUnavailable) {
      return <Typography sx={styles.helperText}>Express status unavailable</Typography>;
    }
    return (
      <>
        <Typography sx={styles.helperText}>
          {grant ? `Ended ${formatDate(grant.endDate)}` : 'This account does not have AirWorks Express.'}
        </Typography>
        {!mode && grantButton}
      </>
    );
  };

  const dateField = (label: string, value: Moment, onChange: (date: Moment) => void, bounds: { minDate?: Moment; maxDate?: Moment }) => (
    <DatePicker
      label={label}
      value={value}
      format="MM/DD/YYYY"
      minDate={bounds.minDate}
      maxDate={bounds.maxDate}
      onChange={(date: Moment | null) => {
        if (date && date.isValid()) onChange(moment(date).startOf('day'));
      }}
      slotProps={{ textField: { variant: 'standard', sx: styles.dateField } }}
    />
  );

  return (
    <Box>
      <Typography variant="h4" sx={styles.sectionTitle}>AirWorks Express</Typography>
      <Divider sx={styles.divider} />
      {renderStatus()}
      {mode && (
        <Box sx={styles.form}>
          {mode !== 'end' && (
            <LocalizationProvider dateAdapter={AdapterMoment}>
              <Box sx={styles.fieldRow}>
                <TextField
                  id="expressAICredits"
                  label={mode === 'change' ? 'Credits total' : 'Credits'}
                  variant="standard"
                  type="number"
                  value={creditsInput}
                  onChange={(e) => setCreditsInput(e.target.value)}
                  InputProps={{ inputProps: { step: 0.01, min: 0.01 } }}
                  sx={styles.creditsField}
                />
                {mode === 'grant' && dateField('Start', startDate, setStartDate, { maxDate: moment() })}
                {dateField('End', endDate, setEndDate, { minDate: moment().startOf('day').add(1, 'day') })}
              </Box>
            </LocalizationProvider>
          )}
          {belowUsed && <Typography variant="body2" sx={styles.error}>{`${formatCredits(used)} credits are already used`}</Typography>}
          <TextField
            id="expressAINote"
            label="Note (required)"
            variant="standard"
            multiline
            minRows={2}
            onChange={(e) => setNote(e.target.value)}
            value={note}
            sx={styles.notesField}
          />
          {error && <Typography variant="body2" sx={styles.error}>{error}</Typography>}
          <Box sx={styles.buttonGroup}>
            <Button sx={styles.planButton} disabled={!canSave} onClick={save} color={mode === 'end' ? 'error' : 'primary'} variant="contained">
              <Fade in={saving}>
                <CircularProgress size={24} sx={styles.buttonProgress} />
              </Fade>
              <Fade in={!saving}>
                <span>{mode === 'end' ? 'End Express' : 'Save'}</span>
              </Fade>
            </Button>
            <Button sx={styles.planButton} onClick={closeForm} variant="text">Cancel</Button>
          </Box>
        </Box>
      )}
      <ConfirmDialog
        showDialog={confirmingEnd}
        contentText={`End AirWorks Express for this account? Its ${formatCredits(balance)} unused credits lapse.`}
        loading={saving}
        onOk={() => submit({ action: 'end', note: note.trim() })}
        onCancel={() => setConfirmingEnd(false)}
      />
      <HistoryList
        entries={history}
        getKey={(entry) => entry._id}
        renderRow={(entry) => (
          <Box sx={styles.historyRow}>
            <Typography variant="body2">{moment.utc(entry.createdAt).format('MM/DD/YYYY')}</Typography>
            <Typography variant="body2" sx={styles.historyAction}>
              {[
                ACTION_LABELS[entry.action] || entry.action,
                typeof entry.credits === 'number' ? `${formatCredits(entry.credits)} credits` : null,
                typeof entry.balanceAfter === 'number' ? `balance ${formatCredits(entry.balanceAfter)}` : null,
              ].filter(Boolean).join(' · ')}
            </Typography>
            <Typography variant="body2">{entry.changedBy}</Typography>
            <Typography variant="body2" sx={styles.historyNotes}>{entry.note}</Typography>
          </Box>
        )}
      />
    </Box>
  );
};

export default ExpressAI;
